import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	TextField,
	Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import {
	getGpsrItemsRequest,
	gpsrReferralFailure,
	gpsrReferralRequest,
} from 'redux/reducers/gpsr/reducer';
import {
	getGpsrItems,
	getGpsrItemsLoad,
	getGpsrReferralError,
	getGpsrReferralLoad,
} from 'redux/reducers/gpsr/selectors';
import { TGpsrItem } from 'redux/reducers/gpsr/types';
import { ESortDirection } from 'ui/EasTable/types';

interface ExtendSubscriptionDialogProps {
	setClose: () => void;
	currentEmId: number;
}

const GPSR_CLIENTS_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 500;

export const ExtendSubscriptionDialog: FC<ExtendSubscriptionDialogProps> = ({
	setClose,
	currentEmId,
}) => {
	const dispatch = useDispatch();
	const gpsrItems = useSelector(getGpsrItems);
	const itemsLoading = useSelector(getGpsrItemsLoad);
	const referralLoading = useSelector(getGpsrReferralLoad);
	const referralError = useSelector(getGpsrReferralError);

	const [selectedClient, setSelectedClient] = useState<TGpsrItem | null>(null);
	const [searchString, setSearchString] = useState('');
	const [debouncedSearchString] = useDebounce(searchString, SEARCH_DEBOUNCE_MS);
	const submittedRef = useRef(false);

	const clientOptions = gpsrItems?.data?.filter((item) => item.emId !== currentEmId) ?? [];

	useEffect(() => {
		dispatch(
			getGpsrItemsRequest({
				pageNumber: 1,
				pageSize: GPSR_CLIENTS_PAGE_SIZE,
				searchString: debouncedSearchString,
				filters: [{ fieldName: 'paymentStatus', value: 'paid', operator: '=' }],
				orderBy: [{ fieldName: 'id', direction: ESortDirection.DESC }],
			}),
		);
	}, [dispatch, debouncedSearchString]);

	useEffect(() => {
		if (!submittedRef.current) return;
		if (referralLoading) return;
		submittedRef.current = false;
		if (!referralError) {
			setClose();
		}
	}, [referralLoading, referralError, setClose]);

	const eraseForm = () => {
		setSelectedClient(null);
		setSearchString('');
		submittedRef.current = false;
		dispatch(gpsrReferralFailure({ error: '' }));
	};

	const handleClose = () => {
		eraseForm();
		setClose();
	};

	const handleSubmit = () => {
		if (!selectedClient) return;
		submittedRef.current = true;
		dispatch(
			gpsrReferralRequest({
				emId: currentEmId,
				referralId: selectedClient.emId,
			}),
		);
	};

	return (
		<Dialog open onClose={handleClose} fullWidth maxWidth="sm">
			<DialogTitle fontWeight="bold" color="primary">
				Extend Subscription
			</DialogTitle>
			<DialogContent>
				<Box sx={{ pt: 1 }}>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Select a GPSR client to use as the referral source for extending this subscription.
					</Typography>
					<FormControl fullWidth>
						<Autocomplete<TGpsrItem>
							options={clientOptions}
							loading={itemsLoading}
							value={selectedClient}
							onChange={(_e, value) => setSelectedClient(value)}
							onInputChange={(_e, value, reason) => {
								if (reason === 'input') setSearchString(value);
							}}
							filterOptions={(options) => options}
							getOptionLabel={(option) => `${option.emName} (EM ${option.emId})`}
							isOptionEqualToValue={(option, value) => option.emId === value.emId}
							renderInput={(params) => (
								<TextField
									{...params}
									label="GPSR client"
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{itemsLoading ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</FormControl>
					{referralError ? (
						<Typography variant="body2" color="error" sx={{ mt: 2 }}>
							{referralError}
						</Typography>
					) : null}
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={referralLoading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={!selectedClient || referralLoading}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};
